<template>
  <div class="expense-list fade-in">
    <!-- ===== LOADER ===== -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </transition>

    <!-- ===== 1️⃣ STATISTICS CARDS ===== -->
    <div>
      <div class="kpi-row">
        <div v-for="(kpi, i) in expenseStats" :key="i" class="kpi-card scale-in">
          <div>
            <h4>{{ kpi.title }}</h4>
            <div class="value">{{ kpi.value }}</div>
          </div>
          <i :class="kpi.icon + ' kpi-icon'"></i>
        </div>
      </div>

      <!-- ===== 2️⃣ FILTERS + ACTIONS ===== -->
      <div class="grid-header fade-in">
        <div class="filters">
          <div class="search-box">
            <i class="bx bx-search"></i>
            <input
              type="text"
              placeholder="Search expenses..."
              v-model="searchQuery"
              @input="fetchExpenses"
            />
          </div>

          <div class="filter-actions">
            <!-- IMPORT -->
            <input
              ref="importFileInput"
              type="file"
              accept=".csv"
              style="display: none"
              @change="handleImport"
            />

            <button class="filter-btn" @click="triggerImport">
              <i class="bx bx-upload"></i> Import
            </button>

            <!-- EXPORT -->
            <button class="filter-btn" @click="exportExpNow">
              <i class="bx bx-download"></i> Export
            </button>

            <!-- SAMPLE -->
            <button class="filter-btn" @click="downloadSampleNow">
              <i class="bx bx-file"></i> Sample
            </button>
          </div>

        </div>

        <button class="btn-primary" @click="addexpnow()">
          <i class="bx bx-plus"></i> Add Expense
        </button>
      </div>

      <!-- ===== 3️⃣ EXPENSE CARDS GRID ===== -->
      <div v-if="expenses.length > 0" class="website-grid fade-in">
        <div
          class="website-card hover-lift"
          v-for="expense in filteredExpenses"
          :key="expense.expanse_id"
        >
          <!-- HEADER -->
          <div class="website-card-header">
            <h4>
              <i class="bx bx-receipt"></i>
              {{ expense.expanse_title }}
            </h4>

            <div class="card-menu">
              <i class="bx bx-dots-vertical-rounded"></i>
              <div class="card-menu-dropdown">
                <button @click="editExpense(expense)">
                  <i class="bx bx-edit-alt"></i> Edit
                </button>
                <button @click="removeExpense(expense.expanse_id)">
                  <i class="bx bx-trash"></i> Delete
                </button>
              </div>
            </div>
          </div>

          <!-- BODY -->
          <div class="website-card-body">
            <div class="expense-meta">
              <div>
                <i class="bx bx-money"></i>
                Amount: <strong>${{ expense.expanse_total }}</strong>
              </div>
              <div>
                <i class="bx bx-category"></i>
                {{ expense.expanse_category || "Uncategorized" }}
              </div>
              <div>
                <i class="bx bx-calendar"></i>
                {{ expense.expanse_date }}
              </div>
            </div>
            <p class="details">
              {{ expense.expanse_notes || "No notes provided" }}
            </p>
          </div>

          <!-- FOOTER -->
          <div class="website-card-footer">
            <span class="website-domain text-muted">#{{ expense.expanse_id }}</span>
            <div class="footer-actions">
              <button class="btn-edit" @click="editExpense(expense)">
                <i class="bx bx-edit-alt"></i> Edit
              </button>
              <button class="btn-delete" @click="removeExpense(expense.expanse_id)">
                <i class="bx bx-trash"></i> Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== EMPTY STATE ===== -->
    
      <div v-else class="empty-state fade-in">
        <i class="bx bx-wallet"></i>
        <h3>No Expenses Found</h3>
        <p>You haven’t created any Expense yet. Let’s set up your first one.</p>
        <button
          class="btn-primary"
          @click="addexpnow"
          style="padding: 8px 14px; font-size: 13px; border-radius: 6px;"
        >
          <i class="bx bx-plus" style="font-size: 14px; color: white; margin-top: .7rem;"></i> Create New Expense
        </button>
      </div>


      <!-- ===== 4️⃣ PAGINATION ===== -->
      <div class="pagination fade-in" v-if="expenses.length > 0">
        <span>Showing {{ expenses.length }} expenses</span>
        <div class="pagination-buttons">
          <button><i class="bx bx-chevron-left"></i></button>
          <button class="active">1</button>
          <button>2</button>
          <button>3</button>
          <button><i class="bx bx-chevron-right"></i></button>
        </div>
      </div>
    </div>
     <ConfirmDialog
  :show="showConfirm"
  :title="confirmTitle"
  :message="confirmMessage"
  @cancel="showConfirm = false"
  @confirmAction="handleConfirm"
/>
  </div>
</template>


<script scoped>
import { ref, computed, onMounted } from "vue";
import { getExps, deleteExp, getExpReport, importExpenses,
  exportExpenses,
  downloadExpenseSample, } from "@/apis/expapis";

import { useToast } from "vue-toastification";
const toast = useToast();
import ConfirmDialog from "@/comps/ConfirmDialog.vue";
const importFileInput = ref(null);

export default {
  name: "ExpanseList",
  components:{
    ConfirmDialog
  },
  setup() {
    const showConfirm = ref(false);
    const confirmTitle = ref("");
    const confirmMessage = ref("");
    let confirmCallback = () => {};

    const openConfirm = (title, message, onConfirm) => {
      confirmTitle.value = title;
      confirmMessage.value = message;
      confirmCallback = onConfirm;
      showConfirm.value = true;
    };

    const handleConfirm = () => {
      showConfirm.value = false;
      if (typeof confirmCallback === "function") confirmCallback();
    };
    const expenses = ref([]);
    const loading = ref(false);
    const searchQuery = ref("");
    const page_no = ref(0);

    const expenseStats = ref([
      { title: "Total Expenses", value: "$0", icon: "bx bx-line-chart" },
      { title: "Top Categories", value: "0", icon: "bx bx-category" },
      { title: "Active Expenses", value: "0", icon: "bx bx-wallet" },
      { title: "Total Vendors", value: "0", icon: "bx bx-user-circle" },
    ]);

    // ✅ Fetch Expenses from API
    const fetchExpenses = async () => {
      try {
        loading.value = true;
        const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};

        const res = await getExps(page_no.value, searchQuery.value, userData.user_id);

        if (res.status && res.records) {
          expenses.value = res.records;

          // Update KPI stats locally
          expenseStats.value[0].value = "$" + totalAmount(res.records).toFixed(2);
          expenseStats.value[1].value = countCategories(res.records);
          expenseStats.value[2].value = res.records.filter(
            (e) => e.expanse_status === "active"
          ).length;
        } else {
          expenses.value = [];
        }
      } catch (err) {
        console.error("fetchExpenses error:", err);
      } finally {
        loading.value = false;
      }
    };

    // ✅ Calculate total expense amount
    const totalAmount = (list) => {
      return list.reduce(
        (sum, item) => sum + parseFloat(item.expanse_total || 0),
        0
      );
    };

    // ✅ Count unique categories
    const countCategories = (list) => {
      const cats = new Set(list.map((i) => i.expanse_category || ""));
      return cats.size;
    };

    // === TRIGGER IMPORT (open file input) ===
const triggerImport = () => {
  importFileInput.value.click();
};

// === HANDLE IMPORT FILE ===
const handleImport = async (e) => {
  const file = e.target.files[0];

  if (!file) return;

  try {
    loading.value = true;

    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
    const res = await importExpenses(file, userData.user_id);

    if (res.status) {
      toast.success("Expenses imported successfully!");
      await fetchExpenses();
    } else {
      toast.error(res.success || "Failed to import expenses");
    }
  } catch (err) {
    console.error("import error:", err);
    toast.error("Error importing expenses");
  } finally {
    loading.value = false;
    importFileInput.value.value = null; // reset input
  }
};

// === EXPORT EXPENSES ===
const exportExpNow = async () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
    const res = await exportExpenses(userData.user_id);

    if (res.status) {
      toast.success("Expenses exported successfully!");
    } else {
      toast.error(res.success || "Failed to export");
    }
  } catch (err) {
    console.error("export error:", err);
    toast.error("Error exporting expenses");
  }
};

// === DOWNLOAD SAMPLE CSV ===
const downloadSampleNow = async () => {
  try {
    const res = await downloadExpenseSample();

    if (res.status) {
      toast.success("Sample downloaded");
    } else {
      toast.error("Failed to download sample");
    }
  } catch (err) {
    console.error("sample error:", err);
    toast.error("Error downloading sample");
  }
};


    // ✅ Delete Expense
    const removeExpense = async (expanse_id) => {
      openConfirm(
        "Delete Expense",
        `Are you sure you want to delete?`,
        async () => {
          try {
            const res = await deleteExp(expanse_id);
            if (res.status) {
              toast.success("Expense deleted successfully");
              expenses.value = expenses.value.filter(
                (e) => e.expanse_id !== expanse_id
              );
            } else {
              toast.warning(res.success || "Failed to delete expense");
            }
          } catch (err) {
            console.error("removeExpense error:", err);
            toast.error("Error deleting expense");
          }
        }
      );
      
    };

    // ✅ Edit Expense
    const editExpense = (expense) => {
      window.location.href = `/add_expanse?id=${expense.expanse_id}`;
    };

    // ✅ Add Expense
    const addexpnow = () => {
      window.location.href = "/add_expanse";
    };

    // ✅ Filtered List
    const filteredExpenses = computed(() => {
      return expenses.value.filter((exp) =>
        exp.expanse_title
          ?.toLowerCase()
          .includes(searchQuery.value.toLowerCase())
      );
    });

    // ✅ On Mounted - Fetch Data + Stats
    onMounted(async () => {
      await fetchExpenses();

      const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
      const res = await getExpReport(userData.user_id + "");

      if (res.status && res.report) {
        expenseStats.value = [
          {
            title: "Total Expenses",
            value: "$" + (res.report.total_expenses ?? 0).toFixed(2),
            icon: "bx bx-line-chart",
          },
          {
            title: "Top Categories",
            value: res.report.category_summary?.length || 0,
            icon: "bx bx-category",
          },
          {
            title: "Active Expenses",
            value: res.report.active_expenses ?? 0,
            icon: "bx bx-wallet",
          },
          {
            title: "Total Vendors",
            value: res.report.vendor_count ?? 0,
            icon: "bx bx-user-circle",
          },
        ];
      }
    });

    return {
      expenses,
      loading,
      searchQuery,
      expenseStats,
      filteredExpenses,
      showConfirm,
      confirmTitle,
      confirmMessage,
      importFileInput,
      openConfirm,
      handleConfirm,
      fetchExpenses,
      removeExpense,
      editExpense,
      addexpnow,
      triggerImport,
      handleImport,
      exportExpNow,
      downloadSampleNow,
    };
  },
};
</script>



<style scoped>
/* ===== ANIMATIONS ===== */
.fade-in {
  animation: fadeIn 0.6s ease forwards;
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.scale-in {
  animation: scaleIn 0.5s ease forwards;
  transform: scale(0.95);
  opacity: 0;
}
@keyframes scaleIn {
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ===== FILTERS ===== */
.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-btn {
  border: 1px solid var(--border);
  background: #fff;
  color: var(--text);
  font-weight: 500;
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.25s ease;
}
.filter-btn:hover {
  background: #f3f4f6;
}

/* ===== EXPENSE CARD META ===== */
.expense-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: var(--muted);
  font-size: 13px;
  margin-bottom: 10px;
}
.expense-meta i {
  color: var(--primary);
  margin-right: 6px;
}
.recurring {
  color: #16a34a;
  font-weight: 600;
}
.one-time {
  color: #6b7280;
  font-weight: 600;
}

/* ===== CARD BODY ===== */
.website-card-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100px;
}
.details {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.5;
}

/* ===== CARD MENU ===== */
.card-menu {
  position: relative;
}
.card-menu i {
  cursor: pointer;
  font-size: 1.3rem;
  color: var(--muted);
  transition: color 0.3s ease;
}
.card-menu i:hover {
  color: var(--primary);
}
.card-menu-dropdown {
  position: absolute;
  right: 0;
  top: 1.8rem;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  display: none;
  flex-direction: column;
  z-index: 20;
}
.card-menu:hover .card-menu-dropdown {
  display: flex;
  animation: fadeIn 0.2s ease forwards;
}
.card-menu-dropdown button {
  border: none;
  background: none;
  text-align: left;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.25s ease;
}
.card-menu-dropdown button:hover {
  background: #f3f4f6;
}

/* ===== CARD HOVER EFFECT ===== */
.hover-lift {
  transition: all 0.3s ease;
}
.hover-lift:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 18px rgba(17, 24, 39, 0.1);
}

/* ===== FOOTER ACTIONS ===== */
.footer-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.btn-edit,
.btn-delete {
  border: none;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.btn-edit {
  background: var(--primary);
  color: #fff;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.2);
}
.btn-edit:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
}
.btn-delete {
  background: #f3f4f6;
  color: var(--text);
}
.btn-delete:hover {
  background: #fee2e2;
  color: #991b1b;
  transform: translateY(-1px);
}

/* ===== PAGINATION ===== */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;
  gap: 10px;
}
.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pagination-buttons button {
  border: none;
  background: #f3f4f6;
  color: var(--text);
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-buttons button.active {
  background: var(--primary);
  color: #fff;
}
.pagination-buttons button:hover {
  background: var(--primary);
  color: #fff;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 992px) {
  .filters {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .filter-actions {
    width: 100%;
    justify-content: space-between;
  }
  .website-grid {
    grid-template-columns: 1fr;
  }
}

/* === 📱 MOBILE RESPONSIVE PATCH: EXPENSE LIST === */
@media (max-width: 992px) {
  /* KPI row becomes 2 per row */
  .kpi-row {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)) !important;
    gap: 1rem !important;
  }

  .grid-header {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 1rem !important;
  }

  .filters {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.8rem !important;
  }

  .search-box {
    width: 100% !important;
  }

  .filter-actions {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 0.6rem !important;
  }

  .filter-actions button {
    flex: 1 1 30% !important;
  }

  .btn-primary {
    width: 100% !important;
    font-size: 0.95rem !important;
  }

  .website-grid {
    grid-template-columns: 1fr !important;
    gap: 1rem !important;
  }

  .website-card {
    padding: 1.2rem !important;
    border-radius: 0.8rem !important;
  }

  .website-card-header h4 {
    font-size: 1.05rem !important;
  }

  .expense-meta {
    flex-direction: column !important;
    gap: 0.4rem !important;
  }

  .details {
    font-size: 0.9rem !important;
    line-height: 1.4 !important;
  }

  .footer-actions {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.6rem !important;
  }

  .footer-actions button {
    width: 100% !important;
  }

  .pagination {
    flex-direction: column !important;
    align-items: center !important;
    text-align: center !important;
    gap: 0.6rem !important;
  }

  .pagination span {
    font-size: 0.9rem !important;
  }

  .pagination-buttons {
    justify-content: center !important;
    flex-wrap: wrap !important;
  }

  .pagination-buttons button {
    min-width: 36px !important;
    height: 36px !important;
    font-size: 0.85rem !important;
  }
}

/* === 📱 SMALL SCREENS (≤600px) === */
@media (max-width: 600px) {
  .add-expense-body {
    padding: 1rem !important;
  }

  .kpi-row {
    grid-template-columns: 1fr !important;
  }

  .filter-actions button {
    flex: 1 1 100% !important;
    font-size: 0.85rem !important;
  }

  .btn-primary {
    font-size: 0.9rem !important;
  }

  .website-card {
    padding: 1rem !important;
  }

  .website-card-header h4 {
    font-size: 1rem !important;
  }

  .details {
    font-size: 0.85rem !important;
  }

  .pagination-buttons button {
    flex: 1 1 20% !important;
  }
}

/* === 📱 EXTRA SMALL DEVICES (≤400px) === */
@media (max-width: 400px) {
  .filter-actions {
    flex-direction: column !important;
  }

  .filter-actions button {
    width: 100% !important;
  }

  .footer-actions button {
    font-size: 0.85rem !important;
  }

  .pagination-buttons button {
    flex: 1 1 25% !important;
  }
}
/* === 📱 PAGINATION RESPONSIVE PATCH === */
@media (max-width: 992px) {
  .pagination {
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0.8rem !important;
    text-align: center !important;
  }

  .pagination span {
    width: 100% !important;
    font-size: 0.9rem !important;
    color: var(--muted, #6b7280);
  }

  .pagination-buttons {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    gap: 0.6rem !important;
    flex-wrap: wrap !important;
  }

  .pagination-buttons button {
    min-width: 38px !important;
    height: 38px !important;
    border-radius: 8px !important;
    font-size: 0.9rem !important;
    font-weight: 500 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
}

@media (max-width: 600px) {
  .pagination {
    gap: 1rem !important;
  }

  .pagination span {
    font-size: 0.85rem !important;
  }

  .pagination-buttons {
    width: 100% !important;
    justify-content: center !important;
  }

  .pagination-buttons button {
    flex: 1 1 20% !important;
    min-width: 40px !important;
    height: 40px !important;
    font-size: 0.85rem !important;
  }
}

@media (max-width: 400px) {
  .pagination-buttons {
    gap: 0.4rem !important;
  }

  .pagination-buttons button {
    flex: 1 1 28% !important;
    padding: 0.5rem 0 !important;
    font-size: 0.8rem !important;
  }

  .pagination span {
    font-size: 0.8rem !important;
  }
}
/* === 🔄 Loader Styling === */
.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text, #374151);
}

.loader {
  width: 42px;
  height: 42px;
  border: 4px solid #e5e7eb;
  border-top-color: var(--primary, #111827);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* === 💬 Empty State === */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: var(--muted, #6b7280);
}

.empty-state i {
  font-size: 3rem;
  color: var(--primary, #111827);
  margin-bottom: 1rem;
}

.empty-state p {
  font-size: 1rem;
  font-weight: 500;
  color: var(--text, #374151);
}

/* === ✨ Smooth State Transitions === */
.fade-in {
  animation: fadeIn 0.5s ease-in-out forwards;
  opacity: 0;
  transform: translateY(8px);
}

@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === 📱 Responsive Patch for Loader & Empty State === */
@media (max-width: 600px) {
  .loader-container,
  .empty-state {
    padding: 2rem 0.8rem !important;
  }

  .empty-state i {
    font-size: 2.5rem !important;
  }

  .empty-state p {
    font-size: 0.9rem !important;
  }

  .loader {
    width: 36px !important;
    height: 36px !important;
  }
}

</style>
